<template>
    <x-page :title="pageTitle">
        <template #header-actions>
            <x-back-button :disabled="isLoading" :display-warning="false" />
            <x-create-button
                :url="`/admin/translation-drafts/create?batch_id=${$route.query.batch_id}`"
                :disabled="Boolean(isLoading && $route.query.batch_id)"
                v-if="can('createTranslationDrafts')"
            />
        </template>

        <x-table :headers="['#', 'Judul', '']" :is-loading="isLoading">
            <tr v-for="(resource, index) in resources" :key="resource.id">
                <td>{{ rowIndex(index, $route.query) }}</td>
                <td class="nowrap">
                    <router-link
                        :to="`/admin/translation-drafts/${resource.id}`"
                        title="Klik untuk lihat detail"
                    >
                        {{ resource.title }}
                    </router-link>
                    <div v-if="resource.translated_content">
                        <x-label color="primary">Translated</x-label>
                    </div>
                </td>
                <td>
                    <x-quick-action-button
                        icon="language"
                        @click="translateResource(resource)"
                        :disabled="isLoading"
                        v-if="can('translateTranslationDrafts')"
                    />
                    <x-quick-edit-button
                        :url="`/admin/translation-drafts/${resource.id}/edit`"
                        :disabled="isLoading"
                        v-if="can('updateTranslationDrafts')"
                    />
                    <x-quick-delete-button
                        @click="deleteResource(resource)"
                        :disabled="isLoading"
                        v-if="can('deleteTranslationDrafts')"
                    />
                </td>
            </tr>
        </x-table>

        <x-pagination
            v-if="shouldRenderPagination"
            :pagination="meta"
            :query="$route.query"
        />
    </x-page>
</template>

<script>
import XCreateButton from "../../components/XCreateButton.vue";
import XQuickDeleteButton from "../../components/XQuickDeleteButton.vue";
import XFormGroup from "../../components/XFormGroup.vue";
import XPage from "../../components/XPage.vue";
import XPagination from "../../components/XPagination.vue";
import XTable from "../../components/XTable.vue";
import {
    deletePrompt,
    handleGeneralXhrError,
    handleValidationXhrError,
    warningPrompt,
} from "../../helpers";
import FilterableMixin from "../../mixins/FilterableMixin";
import PaginationMixin from "../../mixins/PaginationMixin";
import PermissionMixin from "../../mixins/PermissionMixin";
import XQuickEditButton from "../../components/XQuickEditButton.vue";
import XBackButton from "../../components/XBackButton.vue";
import XQuickActionButton from "../../components/XQuickActionButton.vue";
import XLabel from "../../components/XLabel.vue";

const defaultFilters = { batch_id: null };

export default {
    beforeRouteUpdate(to, from, next) {
        this.fetchResources(to);
        next();
    },
    components: {
        XCreateButton,
        XTable,
        XQuickDeleteButton,
        XPage,
        XFormGroup,
        XPagination,
        XQuickEditButton,
        XBackButton,
        XQuickActionButton,
        XLabel,
    },
    computed: {
        batchDisplayName() {
            return this.batch?.display_name || "";
        },
        pageTitle() {
            return this.batch
                ? `Draft Terjemahan ${this.batchDisplayName}`
                : "Draft Terjemahan";
        },
    },
    created() {
        this.preselectFilters({
            ...defaultFilters,
            batch_id: this.$route.query.batch_id,
        });

        if (!this.$route.query.batch_id) {
            this.$router.replace("/admin/translation-drafts");
            return;
        }

        this.fetchBatch(this.$route.query.batch_id);
        this.handleFilter(1, true).catch(() => {
            this.fetchResources();
        });
    },
    data() {
        return {
            batch: null,
            filters: { ...defaultFilters },
            isLoading: false,
            resources: [],
        };
    },
    methods: {
        deleteResource(resource) {
            deletePrompt({
                name: resource.title,
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .delete(`/api/translation-drafts/${resource.id}`)
                        .then(() => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `${resource.title} dihapus!`,
                            });
                            this.fetchResources();
                        });
                    handleGeneralXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
        fetchBatch(id) {
            this.isLoading = true;
            const request = axios
                .get(`/api/translation-upload-batches/${id}`, {
                    params: {
                        without_global_scopes: 1,
                    },
                })
                .then((response) => {
                    this.$set(this, "batch", response.data.data);
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        fetchResources(route = this.$route) {
            this.isLoading = true;
            const request = axios
                .get("/api/translation-drafts", {
                    params: {
                        ...route.query,
                        with: ["batch"],
                        without_global_scopes: 1,
                    },
                })
                .then((response) => {
                    this.$set(this, "resources", response.data.data);
                    this.setMeta(response);
                });
            return handleGeneralXhrError(request).finally(() => {
                this.isLoading = false;
            });
        },
        translateResource(resource) {
            warningPrompt({
                title: "Translate Draft",
                text: `Translate ${resource.title}? Action ini akan dikenakan biaya translasi.`,
                onConfirm: () => {
                    this.isLoading = true;
                    const request = axios
                        .post(
                            `/api/translation-drafts/${resource.id}/translate`
                        )
                        .then(() => {
                            this.$notify({
                                type: "success",
                                title: `Berhasil`,
                                text: `${resource.title} di-translate!`,
                            });
                            this.fetchResources();
                        });
                    handleValidationXhrError(request).finally(() => {
                        this.isLoading = false;
                    });
                },
            });
        },
    },
    mixins: [FilterableMixin, PaginationMixin, PermissionMixin],
};
</script>
